import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { createApolloClient } from 'vue-cli-plugin-apollo/graphql-client';
import {
  InMemoryCacheConfig,
  InMemoryCache,
  IntrospectionFragmentMatcher,
  defaultDataIdFromObject,
} from 'apollo-cache-inmemory';
import { ApolloLink, GraphQLRequest } from 'apollo-link';
import AuthToken from '@/utils/auth-token';
import apolloStateResolvers, { initState } from './apollo-state-resolvers';
import cacheRedirects from './apollo-cache-redirects';
import unionFragmentTypes from '@/queries/unionFragmentTypes';
import resetClient from '@/graphql/reset-client';

import linkHttp from '@/graphql/link-http';
import linkWs from '@/graphql/link-ws';
import linkError from '@/graphql/link-error';
import linkScalars from '@/graphql/link-scalars';
import linkRemoveTypenames from '@/graphql/link-remove-typenames';
import Bugsnag from '@bugsnag/browser';
import linkRequestCounter from '@/graphql/link-request-counter';

// Install the vue plugin
Vue.use(VueApollo);

// Http endpoint
const httpEndpoint = process.env.VUE_APP_GRAPHQL_HTTP as string;

const hasSubscriptionOperation = ({ query: { definitions } }: GraphQLRequest) => {
  return definitions.some(
    definition => definition.kind === 'OperationDefinition' && definition.operation === 'subscription',
  );
};

// https://www.apollographql.com/docs/react/api/link/introduction/
const link =
  ApolloLink.from([
    linkRemoveTypenames,
    linkScalars,
    ApolloLink.split(
      hasSubscriptionOperation,
      linkWs,
      linkHttp,
    ),
    linkError,
    ...(process.env.NODE_ENV == 'production' ? [] : [ linkRequestCounter ]),
  ]);

const inMemoryCacheOptions: InMemoryCacheConfig = {
  addTypename: true,
  cacheRedirects,
  fragmentMatcher: new IntrospectionFragmentMatcher({
    introspectionQueryResultData: unionFragmentTypes,
  }),
  dataIdFromObject(responseObject) {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    switch (responseObject.__typename) {
      case 'Currency': return `Currency:${(responseObject as any).code}`;
      case 'Country': return `Country:${(responseObject as any).code}`;
      case 'UnitOfMeasure': return `UnitOfMeasure:${(responseObject as any).code}`;
      default: return defaultDataIdFromObject(responseObject);
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */
  },
};

// Create apollo client
const { apolloClient } = createApolloClient({
  // You can use `https` for secure connection (recommended in production)
  httpEndpoint,
  // You can use `wss` for secure connection (recommended in production)
  // Use `null` to disable subscriptions
  // wsEndpoint,
  // LocalStorage token
  // tokenName: AUTH_TOKEN,
  // Enable Automatic Query persisting with Apollo Engine
  persisting: false,
  // Use websockets for everything (no HTTP)
  // You need to pass a `wsEndpoint` for this to work
  websocketsOnly: false,
  // Is being rendered on the server?
  ssr: false,
  defaultHttpLink: true,

  // Override default apollo link
  // note: don't override httpLink here, specify httpLink options in the
  // httpLinkOptions property of defaultOptions.
  link,

  // Override default cache
  // cache: myCache

  inMemoryCacheOptions,

  // Override the way the Authorization header is set
  // getAuth: (tokenName) => ...

  // Additional ApolloClient options
  // apollo: { ... }

  // Client local data (see apollo-link-state)
  // clientState: { resolvers: { ... }, defaults: { ... } }
  onCacheInit: (cache: InMemoryCache) => {
    cache.writeData({ data: initState });
  },
  resolvers: apolloStateResolvers,
});

// Create vue apollo provider
export const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
  defaultOptions: {
    $query: {
      fetchPolicy: 'cache-first',
    },
  },
  errorHandler(error) {
    // looks to me like a bad place to handle errors, seems to be called too many times
    // whenever there is an error

    // eslint-disable-next-line no-console
    console.log(
      '%cVueApollo err:',
      'background: green; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;',
      error,
      );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((error.networkError as any)?.statusCode === 401) {
      return false;
    } else {
      Bugsnag.notify(error);
    }
  },
});

// Manually call this when user log in
export function onLogin(token: string): Promise<void> {
  AuthToken.set(token);
  return resetClient(apolloClient);
}

// Manually call this when user log out
export function onLogout(): Promise<void> {
  AuthToken.remove();
  return resetClient(apolloClient);
}

export function onUnitChange(): Promise<void> {
  return resetClient(apolloClient);
}
